import {
  Avatar,
  Box,
  IconButton,
  Link,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useMemo, useState } from "react";
import { getWebDomain } from "../utils";
import { useWhitelabel } from "../context/WhitelabelContext";

const mcpLogo = require("../img/mcp.png");

type Variant = "apiKey" | "personalAccessKey";

interface McpSetupSectionProps {
  variant: Variant;
  sx?: object;
}

type SnippetLang = "bash" | "json" | "toml";

interface SnippetBlock {
  label: string;
  lang: SnippetLang;
  text: string;
}

interface ClientInfo {
  key: string;
  label: string;
  intro?: string;
  paths?: { os: string; path: string }[];
  buildBlocks: (ctx: {
    mcpUrl: string;
    serverName: string;
    headerName: string;
    headerValue: string;
    headerArg: string;
  }) => SnippetBlock[];
}

const jsonHttpConfig = (
  serverName: string,
  mcpUrl: string,
  headerName: string,
  headerValue: string,
) =>
  JSON.stringify(
    {
      mcpServers: {
        [serverName]: {
          url: mcpUrl,
          headers: { [headerName]: headerValue },
        },
      },
    },
    null,
    2,
  );

const jsonStdioConfig = (
  serverName: string,
  mcpUrl: string,
  headerArg: string,
) =>
  JSON.stringify(
    {
      mcpServers: {
        [serverName]: {
          command: "npx",
          args: ["-y", "mcp-remote", mcpUrl, "--header", headerArg],
        },
      },
    },
    null,
    2,
  );

const CLIENTS: ClientInfo[] = [
  {
    key: "claude-code",
    label: "Claude Code",
    intro: "Run this command in your terminal to add the MCP server:",
    buildBlocks: ({ mcpUrl, serverName, headerArg }) => [
      {
        label: "Terminal",
        lang: "bash",
        text: `claude mcp add ${serverName} --transport http ${mcpUrl} --header "${headerArg}"`,
      },
    ],
  },
  {
    key: "codex",
    label: "Codex",
    intro:
      "Add the server to Codex CLI with one command, or edit ~/.codex/config.toml directly. Codex uses TOML — make sure the snippet lives under a [mcp_servers.<name>] header.",
    paths: [{ os: "Config file", path: "~/.codex/config.toml" }],
    buildBlocks: ({ mcpUrl, serverName, headerName, headerValue }) => [
      {
        label: "Terminal (Codex CLI)",
        lang: "bash",
        text: `codex mcp add ${serverName} --transport http --url ${mcpUrl} --header "${headerName}=${headerValue}"`,
      },
      {
        label: "~/.codex/config.toml",
        lang: "toml",
        text: `[mcp_servers.${serverName}]\nurl = "${mcpUrl}"\nhttp_headers = { "${headerName}" = "${headerValue}" }`,
      },
    ],
  },
  {
    key: "cursor",
    label: "Cursor",
    intro:
      "Install with one click via the Cursor CLI, or edit the config file directly. Restart Cursor after saving.",
    paths: [
      { os: "Global (all projects)", path: "~/.cursor/mcp.json" },
      { os: "Per-project", path: ".cursor/mcp.json (in your project root)" },
    ],
    buildBlocks: ({ mcpUrl, serverName, headerName, headerValue, headerArg }) => [
      {
        label: "Terminal (requires Cursor 0.46+)",
        lang: "bash",
        text: `npx -y cursor-mcp-install ${serverName} --url ${mcpUrl} --header "${headerArg}"`,
      },
      {
        label: "~/.cursor/mcp.json",
        lang: "json",
        text: jsonHttpConfig(serverName, mcpUrl, headerName, headerValue),
      },
    ],
  },
  {
    key: "vscode",
    label: "VS Code",
    intro:
      "Requires VS Code 1.102+ with MCP enabled. Add via the CLI, the `MCP: Add Server` command, or by editing the config file.",
    paths: [
      { os: "Workspace", path: ".vscode/mcp.json (in your project root)" },
      {
        os: "User (all workspaces)",
        path: "Command Palette → `MCP: Open User Configuration`",
      },
    ],
    buildBlocks: ({ mcpUrl, serverName, headerName, headerValue }) => [
      {
        label: "Terminal",
        lang: "bash",
        text: `code --add-mcp '${JSON.stringify({
          name: serverName,
          url: mcpUrl,
          headers: { [headerName]: headerValue },
        })}'`,
      },
      {
        label: ".vscode/mcp.json",
        lang: "json",
        text: jsonHttpConfig(serverName, mcpUrl, headerName, headerValue),
      },
    ],
  },
  {
    key: "claude-desktop",
    label: "Claude Desktop",
    intro:
      "Open Settings → Developer → Edit Config (or edit the file directly) and merge the snippet below into claude_desktop_config.json. Restart Claude Desktop after saving.",
    paths: [
      {
        os: "macOS",
        path: "~/Library/Application Support/Claude/claude_desktop_config.json",
      },
      { os: "Windows", path: "%APPDATA%\\Claude\\claude_desktop_config.json" },
    ],
    buildBlocks: ({ mcpUrl, serverName, headerName, headerValue }) => [
      {
        label: "claude_desktop_config.json",
        lang: "json",
        text: jsonHttpConfig(serverName, mcpUrl, headerName, headerValue),
      },
    ],
  },
  {
    key: "stdio",
    label: "stdio / generic client",
    intro:
      "For any MCP client that only speaks stdio, use mcp-remote as a proxy. Test the connection from your terminal, or drop the snippet into the client's MCP config file.",
    buildBlocks: ({ mcpUrl, serverName, headerArg }) => [
      {
        label: "Terminal (test the connection)",
        lang: "bash",
        text: `npx mcp-remote ${mcpUrl} --header "${headerArg}"`,
      },
      {
        label: "MCP config",
        lang: "json",
        text: jsonStdioConfig(serverName, mcpUrl, headerArg),
      },
    ],
  },
];

const McpSetupSection: React.FC<McpSetupSectionProps> = ({ variant, sx = {} }) => {
  const { whitelabelSettings } = useWhitelabel();
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);

  const productName = whitelabelSettings?.title || "the platform";
  const mcpUrl = `${getWebDomain()}/api/v0/mcp`;
  const serverName = "platform";

  const isPak = variant === "personalAccessKey";
  const keyPlaceholder = isPak ? "YOUR_PERSONAL_ACCESS_KEY" : "YOUR_API_KEY";
  const headerName = isPak ? "Authorization" : "X-API-KEY";
  const headerValue = isPak ? `Bearer ${keyPlaceholder}` : keyPlaceholder;
  const headerArg = `${headerName}: ${headerValue}`;

  const activeClient = CLIENTS[tab];
  const activeBlocks = useMemo(
    () =>
      activeClient.buildBlocks({
        mcpUrl,
        serverName,
        headerName,
        headerValue,
        headerArg,
      }),
    [activeClient, mcpUrl, serverName, headerName, headerValue, headerArg],
  );

  const handleCopy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        mb: 2,
        p: 2,
        ...sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Avatar src={mcpLogo} alt="MCP" sx={{ width: 40, height: 40 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Connect to MCP
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use {productName} from Claude, Cursor, VS Code, and other AI tools
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isPak
          ? `Use a personal access key to access all of your companies. Replace ${keyPlaceholder} with a key from the dialog above. Set the X-Company-ID header (or companyId tool argument) to scope calls.`
          : `Use a company API key scoped to this company. Replace ${keyPlaceholder} with a key from the table above.`}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {CLIENTS.map((c) => (
            <Tab key={c.key} label={c.label} />
          ))}
        </Tabs>
      </Box>

      {activeClient.intro && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {activeClient.intro}
        </Typography>
      )}

      {activeClient.paths && (
        <Box sx={{ mb: 1.5 }}>
          {activeClient.paths.map((p) => (
            <Box
              key={p.os}
              sx={{ display: "flex", gap: 1, fontSize: "0.8rem", mb: 0.25 }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, minWidth: 180 }}
              >
                {p.os}:
              </Typography>
              <Box
                component="code"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                  color: "text.primary",
                  wordBreak: "break-all",
                }}
              >
                {p.path}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {activeBlocks.map((block, i) => {
        const copyKey = `${activeClient.key}:${i}`;
        return (
          <Box key={copyKey} sx={{ mb: i < activeBlocks.length - 1 ? 1.5 : 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
            >
              {block.label}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                position: "relative",
                backgroundColor: "background.default",
                p: 1.5,
                pr: 5,
              }}
            >
              <Tooltip title={copied === copyKey ? "Copied" : "Copy"}>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(copyKey, block.text)}
                  sx={{ position: "absolute", top: 4, right: 4 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  color: "text.primary",
                }}
              >
                {block.text}
              </Box>
            </Paper>
          </Box>
        );
      })}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1.5 }}
      >
        Endpoint: <code>{mcpUrl}</code>. Your key is forwarded to the platform API
        on each request and is never stored on the MCP server. See the{" "}
        <Link href={`${getWebDomain()}/docs`} target="_blank" rel="noopener noreferrer">
          API docs
        </Link>{" "}
        for the full list of tools.
      </Typography>
    </Paper>
  );
};

export default McpSetupSection;
